// Types
import { type ObjectGraph } from '@jet/environment/dependencies/object-graph';
import type { IntentController } from '@jet/environment/dispatching';
import { type UserComponentsIntent } from './UserComponentsIntent';

// Jet
import { IntentKind } from '@jet-app/music-classical/intents';
import { requestViewPageWithQuery } from '@jet-app/music-classical/api/request-view-page';
import { NetworkError } from '@jet-app/music-classical/utilities';
import { requestPostAction } from '@jet-app/music-classical/api/request-post-action';
import type { UserComponentsEntity } from '@jet-app/music-classical/models/header';

export interface UserComponentsResult {
    entities?: UserComponentsEntity[];
    secondaryButtonCount?: number;
}

export const UserComponentsIntentController: IntentController<UserComponentsIntent> =
    {
        $intentKind: IntentKind.USER_COMPONENTS_INTENT,

        async perform(
            intent: UserComponentsIntent,
            objectGraph: ObjectGraph,
        ): Promise<UserComponentsResult | undefined> {
            const url = intent?.url;

            if (intent.actionType === 'perform') {
                const apiResponse = await requestPostAction(
                    url,
                    intent?.payload,
                    objectGraph,
                );

                if (!apiResponse?.body) {
                    throw NetworkError.noContent();
                }

                if (url?.includes('/command/library/')) {
                    return {
                        entities: apiResponse?.body?.buttons?.map(
                            (button: any) => {
                                let libraryButton = button?.button;
                                // If the entity is already in library, the action payload will be in deleteButton
                                if (libraryButton?.action?.deleteButton) {
                                    libraryButton =
                                        libraryButton?.action?.deleteButton;
                                }
                                return {
                                    id: libraryButton.action.payload.id,
                                    type: libraryButton.action.payload
                                        .entityType,
                                    inLibrary:
                                        !libraryButton.action.payload.inLibrary,
                                };
                            },
                        ),
                    };
                } else if (url?.includes('/command/favorites/')) {
                    return {
                        // Using .reduce() to ensure that the entities array only contains
                        // favorite button entities.  There may also be add/remove from library
                        // buttons, which will throw errors if treated as a favorite action.
                        entities: apiResponse?.body?.buttons?.reduce(
                            (acc, button: any) => {
                                const favoriteButton = button?.button;

                                // More than one button can exist, like add/remove from library.
                                // Ensure that the favoriting action is only deailing with favoriting.
                                if (favoriteButton.subtype === 'favorite') {
                                    acc.push({
                                        id: favoriteButton.action.payload.id,
                                        type: favoriteButton.action.payload
                                            .entityType,
                                        inFavorites:
                                            !favoriteButton.action.payload
                                                .favorite,
                                    });
                                }

                                return acc;
                            },
                            [],
                        ),
                    };
                }

                return { entities: [] };
            } else {
                const apiResponse: any = await requestViewPageWithQuery(
                    url,
                    objectGraph,
                );

                if (!apiResponse?.body) {
                    throw NetworkError.noContent();
                }

                // Where to look for favorite and library buttons differs depending on the query being made
                const apiSource = url.includes('tracksMetadata')
                    ? apiResponse.body
                    : apiResponse.body?.secondaryButtons;

                // Getting the Library Button
                let libraryButton = apiSource?.find(
                    (button) => button.subtype === 'library',
                );

                // If the entity is already in library, the action payload will be in deleteButton
                if (libraryButton?.action?.deleteButton) {
                    libraryButton = libraryButton?.action?.deleteButton;
                }

                // Getting the Favorites Button
                let favoritesButton = apiSource?.find(
                    (button) => button.subtype === 'favorite',
                );

                const inFavorites = favoritesButton
                    ? !favoritesButton?.action?.payload?.favorite
                    : apiResponse.body?.isLibraryEntityFavorited;

                const trackEntities = apiResponse?.body?.sections?.[0]
                    ?.components?.[0]?.items
                    ? apiResponse?.body?.sections?.[0]?.components?.[0]?.items
                          .map((item) => {
                              if (item.type === 'track') {
                                  return {
                                      id: item.id,
                                      inFavorites: item.inFavorites,
                                      inLibrary: item.inLibrary,
                                      type: item.type,
                                  };
                              }
                              return null;
                          })
                          .filter(Boolean)
                    : [];

                const mainActionButton = libraryButton ?? favoritesButton;

                return {
                    entities: [
                        {
                            id: mainActionButton?.action?.payload?.id,
                            type: mainActionButton?.action?.payload?.entityType,
                            inLibrary: libraryButton
                                ? !libraryButton?.action?.payload?.inLibrary
                                : inFavorites, // The action is to change or toggle the library status, so the current library status is inverse of it
                            inFavorites,
                        },
                        ...trackEntities,
                    ],
                    // Used to determine if a contextual menu should render in a page header
                    // or if a share button should be rendered in it's place.
                    secondaryButtonCount:
                        apiResponse.body?.secondaryButtons?.length || 0,
                };
            }
        },
    };
